import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { InstagramLogo } from '@phosphor-icons/react';
import { useT } from '../../hooks/useTranslation';
import { BOOKING_DISABLED, FALLBACK_EMAIL } from '../../config/maintenance';
import { useInViewOnce } from '../../hooks/useInViewOnce';

const CONTACT_EMAIL = BOOKING_DISABLED ? FALLBACK_EMAIL : 'info@haaziko.hu';

export const Footer = () => {
  const t = useT();
  const [ref, inView] = useInViewOnce<HTMLElement>('-60px');

  const scrollToSection = useCallback((e: React.MouseEvent<HTMLAnchorElement>, hash: string) => {
    e.preventDefault();
    const el = document.querySelector(hash);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.history.pushState(null, '', hash);
  }, []);

  return (
    <footer
      ref={ref}
      className={`text-sand py-16 relative overflow-hidden transition-[opacity,transform] duration-[900ms] ease-out ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      style={{ background: 'radial-gradient(ellipse at 50% 0%, #2d4a35 0%, #1a2d20 100%)' }}
    >
      {/* Grain */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.035]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat',
          backgroundSize: '128px 128px',
        }}
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-4 gap-12">
        <div className="md:col-span-2">
          <Link to="/" className="text-3xl font-serif text-sand mb-4 inline-block hover:scale-105 transition-transform duration-500 origin-left">haaziko.</Link>
          <p className="text-sand/70 max-w-sm">
            {t.footer.tagline}
          </p>
          <div className="flex items-center gap-5 mt-6">
            <a
              href="https://www.instagram.com/ahaaziko/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="haaziko Instagram"
              className="text-sand/45 hover:text-sand transition-colors duration-300"
            >
              <InstagramLogo size={20} />
            </a>
          </div>
        </div>

        <div>
          <h3 className="font-serif text-lg mb-6 tracking-wide">{t.footer.navigation}</h3>
          <ul className="space-y-4">
            {t.footer.links.map((link) => (
              <li key={link.hash}>
                <a
                  href={link.hash}
                  onClick={(e) => scrollToSection(e, link.hash)}
                  className="text-sand/70 hover:text-sand transition-colors relative group inline-block"
                >
                  {link.label}
                  <span className="absolute -bottom-1 left-0 w-0 h-px bg-sand transition-all duration-300 group-hover:w-full" />
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="font-serif text-lg mb-6 tracking-wide">{t.footer.contact}</h3>
          <ul className="space-y-3 text-sand/70">
            <li>{t.footer.address}</li>
            <li><a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-sand transition-colors">{CONTACT_EMAIL}</a></li>
            <li><a href="tel:+36306377352" className="hover:text-sand transition-colors">+36 30 637 7352</a></li>
            <li>
              <a href="https://www.instagram.com/ahaaziko/" target="_blank" rel="noopener noreferrer" className="hover:text-sand transition-colors inline-flex items-center gap-2">
                <InstagramLogo size={14} />
                @ahaaziko
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 mt-16 pt-8 border-t border-sand/20 flex flex-col md:flex-row justify-between items-center md:items-end text-sm text-sand/50">
        {/* A készítő-jelzés a BAL oldalon, a copyright alatt: a jobb alsó sarkot a lebegő
            foglalás-gomb takarja mobilon és kis képernyőn is. */}
        <div className="text-center md:text-left">
          <p>&copy; {new Date().getFullYear()} haaziko. {t.footer.copyright}</p>
          {/* NTAK-regisztráció: szálláshelyként kötelező feltüntetni. Visszafogottan, a lábléc
              alján — hatósági azonosító, nem marketing-üzenet. */}
          <p className="mt-1 text-xs text-sand/45">{t.footer.ntak}</p>
          <a
            href="https://ozone-ai.hu"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mt-1 py-1.5 text-xs text-sand/40 hover:text-sand/70 transition-colors relative group"
          >
            {t.footer.madeBy}
            <span className="absolute bottom-0.5 left-0 w-0 h-px bg-sand/50 transition-all duration-300 group-hover:w-full" />
          </a>
        </div>
        <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-4 md:mt-0">
          {/* py-1: a 20 px-es sormagasság 28-ra nő, hogy a WCAG 24x24-es érintési minimumát
              teljesítse. Az aláhúzás bottom-0-ra kerül, így a szöveghez képest ugyanoda esik. */}
          <Link to="/adatkezeles" className="inline-block py-1 hover:text-sand transition-colors relative group">
            {t.footer.privacy}
            <span className="absolute bottom-0 left-0 w-0 h-px bg-sand transition-all duration-300 group-hover:w-full" />
          </Link>
          {/* Az ÁSZF statikus PDF, mint a házirend — új lapon nyílik. */}
          <a
            href="/aszf.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block py-1 hover:text-sand transition-colors relative group"
          >
            {t.footer.aszf}
            <span className="absolute bottom-0 left-0 w-0 h-px bg-sand transition-all duration-300 group-hover:w-full" />
          </a>
        </div>
      </div>
    </footer>
  );
};
